import {
  EMPLOYMENT_TYPE_CONSULTANT,
  EMPLOYMENT_TYPE_PROJECT_EMPLOYEE,
  EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
  EMPLOYMENT_TYPE_TRAINEE,
} from "model/OrganizationalStructure";
import { PaymentMethod } from "model/Gig";
import { WorkerEmploymentType } from "model/Salary";
import type {
  AddStaffFormData,
  AddStaffValidationRequirements,
  AddStaffValidationState,
} from "./addStaffPanel.contract";
import { isPaymentMethodValueValidOrEmpty } from "../shared/staff.paymentMethod.logic";
import {
  getEmploymentFieldVisibility,
  getVacationFieldVisibility,
} from "../shared/staffEmploymentFieldVisibility.logic";
import {
  hasValidEmployeeNumber,
  hasValidName,
  hasValidOptionalEmail,
  hasValidOptionalEmploymentDays,
  hasValidOptionalPersonalIdentityNumber,
  hasValidOptionalText,
  hasValidPhoneNumber,
  hasValidRequiredText,
  hasValidWorkPercentage as isValidWorkPercentage,
  hasValue,
} from "../shared/staffInputValidation.logic";

const MONTHLY_SALARY_TYPE = "Monthly";

interface AddStaffValidationParams {
  readonly canSelectSimflexityEmployee?: boolean;
  readonly canSelectSimflexityPay?: boolean;
  readonly formData: AddStaffFormData;
  readonly requirements: AddStaffValidationRequirements;
}

interface ContractValidationValues {
  readonly hasValidAgreement: boolean;
  readonly hasValidNoticeDays: boolean;
  readonly hasValidVacationDays: boolean;
  readonly hasValidVacationEarningDays: boolean;
  readonly hasValidWorkplace: boolean;
}

interface EmploymentValidationValues extends ContractValidationValues {
  readonly hasValidEmployeeNumber: boolean;
  readonly hasValidPaymentMethod: boolean;
  readonly hasValidSalaryAmount: boolean;
  readonly hasValidWorkPercentage: boolean;
}

interface EmploymentTypeFlags {
  readonly isConsultant: boolean;
  readonly isIntroductoryEmployee: boolean;
  readonly isProjectEmployee: boolean;
  readonly isSimflexityEmployee: boolean;
  readonly isTrainee: boolean;
}

interface EmploymentInputValidationParams {
  readonly formData: AddStaffFormData;
  readonly requirements: AddStaffValidationRequirements;
  readonly validationValues: EmploymentValidationValues;
}

interface RequiredEmploymentValidationParams extends EmploymentInputValidationParams {
  readonly canSelectSimflexityEmployee: boolean;
  readonly canSelectSimflexityPay: boolean;
}

function isPositiveNumber(value?: string): boolean {
  if (!value || value.trim().length === 0) return false;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0;
}

function hasInvalidDateRange(formData: AddStaffFormData): boolean {
  if (!formData.startDate || !formData.endDate) return false;
  return !formData.endDate.isAfter(formData.startDate);
}

function isSimflexityEmployee(formData: AddStaffFormData): boolean {
  return formData.employmentType === EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE;
}

export function isValidPhoneNumber(phoneNumber: string): boolean {
  return hasValidPhoneNumber({ value: phoneNumber });
}

function getEmploymentTypeFlags(
  formData: AddStaffFormData,
): EmploymentTypeFlags {
  const employmentType = formData.employmentType;
  return {
    isConsultant: employmentType === EMPLOYMENT_TYPE_CONSULTANT,
    isIntroductoryEmployee:
      employmentType === WorkerEmploymentType.INTRODUCTORY,
    isProjectEmployee: employmentType === EMPLOYMENT_TYPE_PROJECT_EMPLOYEE,
    isSimflexityEmployee:
      employmentType === EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
    isTrainee: employmentType === EMPLOYMENT_TYPE_TRAINEE,
  };
}

export function getAddStaffValidationRequirements(
  formData: AddStaffFormData,
): AddStaffValidationRequirements {
  const employmentTypeFlags = getEmploymentTypeFlags(formData);
  const isEmployeeNumberRequired =
    !employmentTypeFlags.isConsultant &&
    !employmentTypeFlags.isTrainee &&
    !employmentTypeFlags.isSimflexityEmployee;
  // Hidden fields are never required, so requirements follow field visibility.
  const visibility = getEmploymentFieldVisibility(formData.employmentType);

  return {
    isEmployeeNumberRequired,
    isEmployeeCategoryRequired: visibility.showEmployeeCategory,
    isSalaryModelRequired: visibility.showSalaryType,
    isSalaryAmountRequired: visibility.showSalary,
    isWorkPercentageRequired:
      visibility.showSalaryType && formData.salaryType === MONTHLY_SALARY_TYPE,
    isTaxTableRequired: visibility.showTaxTable,
    isPaymentTypeRequired: visibility.showPaymentType,
    isEndDateRequired:
      employmentTypeFlags.isIntroductoryEmployee ||
      employmentTypeFlags.isProjectEmployee ||
      employmentTypeFlags.isTrainee,
  };
}

function hasRequiredWorkerValues(formData: AddStaffFormData): boolean {
  if (!hasValidName({ value: formData.firstName })) return false;
  if (!hasValidName({ value: formData.lastName })) return false;
  if (!hasValidRequiredText({ value: formData.role })) return false;
  if (!hasValidOptionalEmail({ value: formData.email })) return false;
  if (
    !hasValidOptionalPersonalIdentityNumber({
      value: formData.personalIdentityNumber,
    })
  )
    return false;
  return formData.teamIds.length > 0;
}

/**
 * Company policy rules:
 * - Starter subscriptions cannot create SimflexityEmployee.
 * - SimflexityEmployee cannot use Monthly salary model and must use SimflexityPay.
 * - Other employment types can only use SimflexityPay if policy allows it.
 */
function hasValidCompanyPolicyFields({
  canSelectSimflexityEmployee,
  canSelectSimflexityPay,
  formData,
}: {
  readonly canSelectSimflexityEmployee: boolean;
  readonly canSelectSimflexityPay: boolean;
  readonly formData: AddStaffFormData;
}): boolean {
  const isSimflexity = isSimflexityEmployee(formData);

  if (isSimflexity && !canSelectSimflexityEmployee) return false;
  if (isSimflexity && formData.salaryType === MONTHLY_SALARY_TYPE) return false;
  if (isSimflexity && formData.paymentType !== PaymentMethod.SIMFLEXITY_PAY) {
    return false;
  }
  if (!isSimflexity && formData.paymentType === PaymentMethod.SIMFLEXITY_PAY) {
    return canSelectSimflexityPay;
  }

  return true;
}

function hasRequiredEmploymentType(formData: AddStaffFormData): boolean {
  return hasValue(formData.employmentType);
}

function hasRequiredSalaryFields({
  formData,
  hasValidSalaryAmount,
  hasValidWorkPercentage,
  requirements,
}: {
  readonly formData: AddStaffFormData;
  readonly hasValidSalaryAmount: boolean;
  readonly hasValidWorkPercentage: boolean;
  readonly requirements: AddStaffValidationRequirements;
}): boolean {
  if (requirements.isSalaryModelRequired && !hasValue(formData.salaryType)) {
    return false;
  }

  if (requirements.isSalaryAmountRequired && !hasValidSalaryAmount)
    return false;

  if (!hasValidWorkPercentage) return false;

  return true;
}

function hasRequiredCompensationFields({
  formData,
  hasValidPaymentMethod,
  requirements,
}: {
  readonly formData: AddStaffFormData;
  readonly hasValidPaymentMethod: boolean;
  readonly requirements: AddStaffValidationRequirements;
}): boolean {
  if (requirements.isTaxTableRequired && !hasValue(formData.taxTable))
    return false;

  if (!hasValidPaymentMethod) return false;

  if (requirements.isPaymentTypeRequired && !hasValue(formData.paymentType)) {
    return false;
  }

  return true;
}

function hasRequiredClassificationFields({
  formData,
  hasValidEmployeeNumber: isEmployeeNumberValid,
  requirements,
}: {
  readonly formData: AddStaffFormData;
  readonly hasValidEmployeeNumber: boolean;
  readonly requirements: AddStaffValidationRequirements;
}): boolean {
  if (
    requirements.isEmployeeCategoryRequired &&
    !hasValue(formData.employeeCategory)
  ) {
    return false;
  }

  if (!isEmployeeNumberValid) return false;

  return true;
}

function hasRequiredDateFields({
  formData,
  requirements,
}: {
  readonly formData: AddStaffFormData;
  readonly requirements: AddStaffValidationRequirements;
}): boolean {
  if (!formData.startDate) return false;

  if (requirements.isEndDateRequired && !formData.endDate) return false;

  if (formData.endDate && hasInvalidDateRange(formData)) return false;

  return true;
}

function hasRequiredEmploymentValues({
  canSelectSimflexityEmployee,
  canSelectSimflexityPay,
  formData,
  requirements,
  validationValues,
}: RequiredEmploymentValidationParams): boolean {
  return (
    hasRequiredEmploymentInputs({
      formData,
      requirements,
      validationValues,
    }) &&
    hasRequiredCompensationInputs({
      canSelectSimflexityEmployee,
      canSelectSimflexityPay,
      formData,
      requirements,
      validationValues,
    }) &&
    hasValidContractValues(validationValues)
  );
}

function hasRequiredEmploymentInputs({
  formData,
  requirements,
  validationValues,
}: EmploymentInputValidationParams): boolean {
  return (
    hasRequiredEmploymentType(formData) &&
    hasRequiredClassificationFields({
      formData,
      hasValidEmployeeNumber: validationValues.hasValidEmployeeNumber,
      requirements,
    }) &&
    hasRequiredDateFields({ formData, requirements })
  );
}

function hasRequiredCompensationInputs({
  canSelectSimflexityEmployee,
  canSelectSimflexityPay,
  formData,
  requirements,
  validationValues,
}: RequiredEmploymentValidationParams): boolean {
  return (
    hasRequiredSalaryFields({
      formData,
      hasValidSalaryAmount: validationValues.hasValidSalaryAmount,
      hasValidWorkPercentage: validationValues.hasValidWorkPercentage,
      requirements,
    }) &&
    hasRequiredCompensationFields({
      formData,
      hasValidPaymentMethod: validationValues.hasValidPaymentMethod,
      requirements,
    }) &&
    hasValidCompanyPolicyFields({
      canSelectSimflexityEmployee,
      canSelectSimflexityPay,
      formData,
    })
  );
}

function hasValidContractValues(
  validationValues: EmploymentValidationValues,
): boolean {
  return (
    validationValues.hasValidAgreement &&
    validationValues.hasValidNoticeDays &&
    validationValues.hasValidVacationDays &&
    validationValues.hasValidVacationEarningDays &&
    validationValues.hasValidWorkplace
  );
}

function getContractValidationValues(
  formData: AddStaffFormData,
): ContractValidationValues {
  const visibility = getEmploymentFieldVisibility(formData.employmentType);
  const shouldValidateContractTerms = visibility.showContractTerms;
  const shouldValidateVacationEarningDays = getVacationFieldVisibility(
    formData.salaryType,
  ).showVacationEarningDays;
  return {
    hasValidAgreement:
      !shouldValidateContractTerms ||
      hasValidOptionalText({ value: formData.agreement }),
    hasValidNoticeDays:
      !shouldValidateContractTerms ||
      hasValidOptionalEmploymentDays({ value: formData.noticeDays }),
    hasValidVacationDays:
      !shouldValidateContractTerms ||
      hasValidOptionalEmploymentDays({ value: formData.vacationDays }),
    hasValidVacationEarningDays:
      !shouldValidateVacationEarningDays ||
      [
        formData.maxSickLeaveDays,
        formData.maxCareOfChildDays,
        formData.maxParentalLeaveDays,
        formData.advanceVacationDays,
      ].every((value) => hasValidOptionalEmploymentDays({ value })),
    hasValidWorkplace:
      !shouldValidateContractTerms ||
      hasValidOptionalText({ value: formData.workplace }),
  };
}

function getEmploymentValidationValues({
  formData,
  requirements,
}: {
  readonly formData: AddStaffFormData;
  readonly requirements: AddStaffValidationRequirements;
}): EmploymentValidationValues {
  return {
    ...getContractValidationValues(formData),
    hasValidEmployeeNumber: hasValidEmployeeNumber({
      isRequired: requirements.isEmployeeNumberRequired,
      value: formData.employeeNumber,
    }),
    hasValidPaymentMethod: isPaymentMethodValueValidOrEmpty(
      formData.paymentMethodType,
      formData.paymentMethodValue,
    ),
    hasValidSalaryAmount: isPositiveNumber(formData.salaryAmount),
    hasValidWorkPercentage:
      !requirements.isWorkPercentageRequired ||
      isValidWorkPercentage({ value: formData.workPercentage }),
  };
}

export function getAddStaffValidationState({
  canSelectSimflexityEmployee = true,
  canSelectSimflexityPay = false,
  formData,
  requirements,
}: AddStaffValidationParams): AddStaffValidationState {
  const validationValues = getEmploymentValidationValues({
    formData,
    requirements,
  });
  return {
    hasAllRequiredValues:
      hasRequiredWorkerValues(formData) &&
      hasRequiredEmploymentValues({
        canSelectSimflexityEmployee,
        canSelectSimflexityPay,
        formData,
        requirements,
        validationValues,
      }),
    hasValidAgreement: validationValues.hasValidAgreement,
    hasValidEmployeeNumber: validationValues.hasValidEmployeeNumber,
    hasValidEmail: hasValidOptionalEmail({ value: formData.email }),
    hasValidFirstName: hasValidName({ value: formData.firstName }),
    hasValidLastName: hasValidName({ value: formData.lastName }),
    hasValidNoticeDays: validationValues.hasValidNoticeDays,
    hasValidPaymentMethod: validationValues.hasValidPaymentMethod,
    hasValidPersonalIdentityNumber: hasValidOptionalPersonalIdentityNumber({
      value: formData.personalIdentityNumber,
    }),
    hasValidRole: hasValidRequiredText({ value: formData.role }),
    hasValidSalaryAmount: validationValues.hasValidSalaryAmount,
    hasValidVacationDays: validationValues.hasValidVacationDays,
    hasValidVacationEarningDays: validationValues.hasValidVacationEarningDays,
    hasValidWorkPercentage: validationValues.hasValidWorkPercentage,
    hasValidWorkplace: validationValues.hasValidWorkplace,
  };
}
